import { approveAccessRequest, declineAccessRequest, listAccessRequests, type CoworkAccessRequest } from "@/cowork/api"
import { SettingsListV2 } from "@/components/settings-v2/parts/list"
import { SettingsRowV2 } from "@/components/settings-v2/parts/row"
import { useLanguage } from "@/context/language"
import { formatDateTime } from "@/utils/date"
import { createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkAccessRequestsPage() {
  const language = useLanguage()
  const [state, setState] = createStore({
    filter: "requested" as "requested" | "all",
    busy: "",
    error: "",
    notice: "",
    names: {} as Record<string, string>,
  })
  const [requests, actions] = createResource(() => state.filter, listAccessRequests)

  const run = async (
    request: CoworkAccessRequest,
    action: () => Promise<CoworkAccessRequest>,
    noticeKey: NoticeKey,
  ) => {
    if (state.busy) return
    setState({ busy: request.id, error: "", notice: "" })
    try {
      await action()
      setState("notice", language.t(noticeKey, { email: request.email }))
      await actions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("busy", "")
    }
  }

  const approve = (request: CoworkAccessRequest) =>
    run(
      request,
      () => approveAccessRequest(request.id, state.names[request.id]?.trim()),
      "cowork.accessRequests.approved",
    )
  const decline = (request: CoworkAccessRequest) =>
    run(request, () => declineAccessRequest(request.id), "cowork.accessRequests.declined")

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1100px]">
        <header class="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.accessRequests.title")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.accessRequests.description")}</p>
          </div>
          <select
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular text-v2-text-text-base outline-none"
            value={state.filter}
            aria-label={language.t("cowork.accessRequests.filter")}
            onChange={(event) => setState("filter", event.currentTarget.value as typeof state.filter)}
          >
            <option value="requested">{language.t("cowork.accessRequests.filter.pending")}</option>
            <option value="all">{language.t("cowork.accessRequests.filter.all")}</option>
          </select>
        </header>

        <div class="mt-7 space-y-7">
          <Show when={state.notice || state.error}>
            <SettingsListV2>
              <SettingsRowV2
                title={
                  state.error ? language.t("cowork.accessRequests.error") : language.t("cowork.accessRequests.done")
                }
                description={state.error || state.notice}
              >
                <span />
              </SettingsRowV2>
            </SettingsListV2>
          </Show>

          <section>
            <SettingsListV2>
              <Show
                when={!requests.error}
                fallback={
                  <SettingsRowV2
                    title={language.t("cowork.accessRequests.error")}
                    description={requests.error instanceof Error ? requests.error.message : String(requests.error)}
                  >
                    <span />
                  </SettingsRowV2>
                }
              >
                <Show
                  when={!requests.loading && (requests()?.length ?? 0) > 0}
                  fallback={
                    <SettingsRowV2
                      title={
                        requests.loading
                          ? language.t("cowork.accessRequests.loading")
                          : language.t("cowork.accessRequests.empty")
                      }
                      description={language.t("cowork.accessRequests.emptyDescription")}
                    >
                      <span />
                    </SettingsRowV2>
                  }
                >
                  <For each={requests() ?? []}>
                    {(request) => (
                      <SettingsRowV2
                        title={request.firm_name || request.email}
                        description={`${request.email} · ${request.locale} · ${formatDateTime(request.created_at, { dateStyle: "short" })}`}
                      >
                        <div class="flex flex-wrap items-center justify-end gap-2">
                          <Show
                            when={request.status === "requested"}
                            fallback={
                              <span class="rounded-md border border-v2-border-border-base px-2 py-1 text-[11px] capitalize text-v2-text-text-muted">
                                {language.t(statusKey(request.status))}
                              </span>
                            }
                          >
                            <input
                              class="h-8 w-48 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-[12px] text-v2-text-text-base outline-none"
                              placeholder={request.firm_name || language.t("cowork.accessRequests.workspaceName")}
                              aria-label={language.t("cowork.accessRequests.workspaceName")}
                              value={state.names[request.id] ?? ""}
                              disabled={!!state.busy}
                              onInput={(event) => setState("names", request.id, event.currentTarget.value)}
                            />
                            <button
                              type="button"
                              class="h-8 rounded-md bg-v2-background-bg-contrast px-3 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                              disabled={!!state.busy}
                              onClick={() => void approve(request)}
                            >
                              {state.busy === request.id
                                ? language.t("cowork.accessRequests.working")
                                : language.t("cowork.accessRequests.approve")}
                            </button>
                            <button
                              type="button"
                              class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px] text-v2-text-text-muted hover:text-v2-text-text-base disabled:opacity-50"
                              disabled={!!state.busy}
                              onClick={() => void decline(request)}
                            >
                              {language.t("cowork.accessRequests.decline")}
                            </button>
                          </Show>
                        </div>
                      </SettingsRowV2>
                    )}
                  </For>
                </Show>
              </Show>
            </SettingsListV2>
          </section>
        </div>
      </div>
    </div>
  )
}

type NoticeKey = "cowork.accessRequests.approved" | "cowork.accessRequests.declined"

function statusKey(status: CoworkAccessRequest["status"]) {
  return (
    {
      requested: "cowork.accessRequests.status.requested",
      approved: "cowork.accessRequests.status.approved",
      declined: "cowork.accessRequests.status.declined",
    } as const
  )[status]
}
